<template src="./check-group.html"></template>

<script lang="ts">
import fieldGroupPropsMixin from './FieldGroupPropsMixin.vue'
import FieldGroup from './FieldGroup.vue'


const CHECKBOX_TYPE = 'checkbox'
const RADIO_TYPE = 'radio'
const ICON_TYPE = 'icon'
const IMAGE_TYPE = 'image'
const TAG_TYPE = 'tag'
const DISPLAY_TYPES = [CHECKBOX_TYPE, RADIO_TYPE, ICON_TYPE, IMAGE_TYPE, TAG_TYPE]
const DISPLAY_TYPE_CLASSES = {
  [CHECKBOX_TYPE]: 'check',
  [RADIO_TYPE]: 'radio',
  [ICON_TYPE]: 'radio-icon',
  [IMAGE_TYPE]: 'radio-image',
  [TAG_TYPE]: 'check-tag'
}

export default {
  components: { 'tt-field-group': FieldGroup },
  data() {
    return {
      value: '',  // for radio button (single)
      values: [] // for checkbox (multiple)
    }
  },
  props: {
    describedbyId: {
      type: String,
      default: () => `tt-hint-${Math.random().toString(16).slice(5)}`
    },
    displayInline: {
      type: Boolean,
      default: true
    },
    // 'icon' and 'image' are single-select (radio), tag is a multi-select (checkbox)
    displayType: {
      type: String,
      validator: (value: string) => (DISPLAY_TYPES.indexOf(value) > -1)
    },
    groupName: { type: String, required: true },
    fields: {
      // For each element:
      //  'label' & 'value' are required
      //  'iconName' is required if displayType is 'icon'
      //  'imagePath' is required if displayType is 'image'
      // optional: id, disabled
      type: Array,
      required: true
    },
    modelValue: [Array, String, Number, Boolean]
  },
  emits: ['update:modelValue'],
  mixins: [fieldGroupPropsMixin],
  computed: {
    classType() {
      return DISPLAY_TYPE_CLASSES[this.displayType]
    },
    decoratedFields() {
      const labelReqSpan = this.isSingleCheckbox && this.required ?
        ` <span class="tt-o-check__required">${this.$msg('fieldGroup.requiredIndicator')}</span>`
        : ''
      return this.fields.map((field) => {
        const isChecked = this.isRadio ? this.value === field.value :
          this.values && this.values.indexOf(field.value) > -1
        return {
          id: field.id || `tt-${this.classType}-${Math.random().toString(16).slice(5)}`,
          disabled: field.disabled || false,
          label: field.label + labelReqSpan,
          value: field.value,
          iconName: field.iconName,
          imagePath: field.imagePath,
          checked: isChecked
        }
      })
    },
    groupClasses() {
      return !this.displayInline ? ['tt-o-check-group--vertical', 'tt-u-spacing--md'] : ''
    },
    inputClass() {
      return `tt-o-${this.classType}`
    },
    isCheckboxType() {
      return this.displayType === CHECKBOX_TYPE || this.displayType === TAG_TYPE
    },
    isCheckboxGroup() {
      return this.isCheckboxType && this.fields.length > 1
    },
    isIconType() {
      return this.displayType === ICON_TYPE
    },
    isImageType() {
      return this.displayType === IMAGE_TYPE
    },
    isRadio() {
      return ~[ICON_TYPE, IMAGE_TYPE, RADIO_TYPE].indexOf(this.displayType)
    },
    isSingleCheckbox() {
      return this.isCheckboxType && this.fields.length === 1
    },
    itemClass() {
      return `tt-o-${this.classType}__item`
    },
    itemLabelClass() {
      return `tt-o-${this.classType}__label`
    }
  },
  methods: {
    setValues() {
      if (this.modelValue !== undefined) {
        if (this.isCheckboxGroup) {
          this.values = this.modelValue
        } else {
          this.value = this.modelValue
        }
      }
    }
  },
  watch: {
    // May be an array and in Vue 3, arrays in watch must have deep: true
    model: {
      handler() {
        this.setValues()
      },
      deep: true
    },
    value() {
      if (this.isRadio || this.isSingleCheckbox) {
        this.$emit('update:modelValue', this.value)
      }
    },
    // in Vue 3, arrays in watch must have deep: true
    values: {
      handler() {
        if (this.isCheckboxGroup) {
          this.$emit('update:modelValue', this.values)
        }
      },
      deep: true
    }
  },
  created() {
    this.setValues()
  }
}
</script>

<style lang="scss" src="./check-group.scss"></style>
